import { observer } from 'mobx-react-lite';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { useTranslation } from 'react-i18next';
import { useAdminActivityMetrics } from '../../../hooks/useAdminQuery';
import {
  PeopleAlt,
  School,
  Class,
  Login,
  MenuBook,
  BarChart,
  Dashboard,
  Person,
  SupervisedUserCircle,
} from '@mui/icons-material';
import { ROLES, SchoolActivity } from '@shared/types';
import StatCard from '../../components/StatCard';

const OverviewPage = observer(() => {
  const { t, i18n } = useTranslation();
  const adminStore = useStoreByName('adminStore');
  const userStore = useStoreByName('userStore');
  // Don't destructure observable properties - access directly for better reactivity
  // const { comprehensiveActivityData, selectedSchoolId } = adminStore;

  // Get loading state from TanStack Query hook - fetch all data once, filter client-side
  const { isLoading, isFetching } = useAdminActivityMetrics();
  const isDataLoading = isLoading || isFetching;
  const isRtl = i18n.language === 'he';

  // בדיקת סוג המשתמש
  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isAdmin = userRoleId === ROLES.ADMIN; // ADMIN
  const isPrincipal = userRoleId === ROLES.PRINCIPAL; // PRINCIPAL

  // Access observables directly to ensure reactivity
  const data = adminStore.comprehensiveActivityData;
  const selectedSchoolId = adminStore.selectedSchoolId;

  // Calculate metrics with proper MobX reactivity
  const metrics = (() => {
    if (!data) {
      return {
        totalSchools: 0,
        totalClasses: 0,
        totalTeachers: 0,
        totalStudents: 0,
        totalPrincipals: 0,
        activeStudents: 0,
        totalReadings: 0,
        averageReadingsPerStudent: 0,
      };
    }

    // When a specific school is selected
    if (selectedSchoolId && data.schools?.length > 0) {
      // Find the selected school in the data
      const school = data.schools.find((s: any) => String(s.id) === String(selectedSchoolId));
      
      if (school) {
        const totalTeachers = school.teachers?.length || 0;
        const totalStudents =
          school.classes?.reduce((sum: number, cls: any) => sum + (cls.students?.length || 0), 0) || 0;

        return {
          totalSchools: 1,
          totalClasses: school.classes?.length || 0,
          totalTeachers,
          totalStudents,
          totalPrincipals: data.overview?.totalPrincipals || 0,
          activeStudents: school.activity?.activeStudents || 0,
          totalReadings: school.activity?.totalReadings || 0,
          averageReadingsPerStudent: school.activity?.averageReadingsPerStudent || 0,
        };
      }
    }

    // When viewing all schools or multiple schools
    return {
      totalSchools: data.overview?.totalSchools || 0,
      totalClasses: data.overview?.totalClasses || 0,
      totalTeachers: data.overview?.totalTeachers || 0,
      totalStudents: data.overview?.totalStudents || 0,
      totalPrincipals: data.overview?.totalPrincipals || 0,
      activeStudents: data.systemActivity?.activeStudents || 0,
      totalReadings: data.systemActivity?.totalReadings || 0,
      averageReadingsPerStudent: data.systemActivity?.averageReadingsPerStudent || 0,
    };
  })();

  // Data loading is now handled by AdminPanelLayout using TanStack Query hooks
  // No manual loading needed here - data is automatically available via adminStore

  return (
    <div className="overviewContainer" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Header Section */}
      <div className="head-line">
        <h1 className="title">
          {t('overview')} <Dashboard />
        </h1>
        <p className="subtitle">
          {selectedSchoolId ? (
            <>
              {isPrincipal ? t('schoolOverviewDescriptionFor') : t('systemOverviewDescriptionFor')}{' '}
              <span style={{ fontWeight: 'bold', color: '#667eea' }}>
                {adminStore.availableSchools.find((school: SchoolActivity) => String(school.id) === String(selectedSchoolId))?.name || t('selectedSchool')}
              </span>
            </>
          ) : (
            <>
              {isPrincipal ? t('schoolOverviewDescriptionAll') : t('systemOverviewDescriptionAll')}{' '}
              <span style={{ fontWeight: 'bold', color: '#4caf50' }}>{t('allSchools')}</span>
            </>
          )}
        </p>
      </div>

      {/* KPI Section */}
      <div className="kpiSection">
        <div className="kpiGrid">
          {/* First row: Principals (admin only), Schools, Teachers, Classes, Students */}
          {isAdmin && !selectedSchoolId && (
            <StatCard
              title={t('principals')}
              value={metrics.totalPrincipals || 0}
              icon={<SupervisedUserCircle />}
              variant="indigo"
              isLoading={isDataLoading}
            />
          )}

          {/* Show schools only when viewing all schools */}
          {!selectedSchoolId && (
            <StatCard
              title={t('schools')}
              value={metrics.totalSchools}
              icon={<School />}
              variant="secondary"
              isLoading={isDataLoading}
            />
          )}

          {/* Teachers */}
          <StatCard
            title={t('teachers')}
            value={metrics.totalTeachers}
            icon={<Person />}
            variant="purple"
            isLoading={isDataLoading}
          />

          {/* Classes */}
          <StatCard
            title={t('classes')}
            value={metrics.totalClasses}
            icon={<Class />}
            variant="orange"
            isLoading={isDataLoading}
          />

          {/* Students - most important metric gets primary brand color */}
          <StatCard
            title={t('students')}
            value={metrics.totalStudents}
            icon={<PeopleAlt />}
            variant="primary"
            isLoading={isDataLoading}
          />
        </div>
      </div>

      {/* Activity Section */}
      <div className="activitySection">
        <h2 className="sectionTitle">
          {t('activityMetrics')} (30 {t('lastDays')})
        </h2>

        <div className="activityGrid">
          <StatCard
            title={t('activeStudents')}
            value={metrics.activeStudents}
            icon={<Login />}
            variant="cyan"
            isLoading={isDataLoading}
          />

          <StatCard
            title={t('totalReadings')}
            value={metrics.totalReadings}
            icon={<MenuBook />}
            variant="secondary"
            isLoading={isDataLoading}
          />

          <StatCard
            title={t('avgReadingsPerStudent')}
            value={metrics.averageReadingsPerStudent}
            icon={<BarChart />}
            variant="success"
            isLoading={isDataLoading}
          />
        </div>
      </div>
    </div>
  );
});

export default OverviewPage;
